import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";

import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import ReportRevisionNotice from "../../../../Components/v2/Accounting/ReportRevisionNotice";
import FormDateRange from "../../../../Components/v2/Form/FormDateRange";
import Modal from "../../../../Components/v2/Modal";
import TableSkeleton from "../../../../Components/TableSkeleton";
import ErrorStateTable from "../../../../Components/ErrorStateTable";

import { cancelAPIRequest, getAccountingFileName } from "../../../../Utils";
import {
  clearExportIncomeStatement,
  requestExportIncomeStatement,
  requestListIncomeStatement,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import {
  accountCategoryLabel,
  EXPORTS,
} from "../../../../Constants/accountingReport";
import {
  wibCurrentMonth,
  wibEndOfDay,
  wibStartOfDay,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingIncomeStatement = () => {
  const { t } = useTranslation();
  const { user } = useAuthState();

  const [dateRange, setDateRange] = useState(wibCurrentMonth);
  const [startDate, endDate] = dateRange;

  // Export & download state
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const accountingDispatch = useAccountingDispatch();

  const {
    request,
    incomeStatementAccounts: storedAccounts,
    incomeStatementSummary: storedSummary,
    incomeStatementLoading,
    incomeStatementError,
    incomeExportLoading,
    incomeExportError,
    incomeExportFile,
  } = useAccountingState();

  // With the range cleared nothing is requested, so the store still holds the
  // previous period. Show nothing rather than present it under an empty picker.
  const hasRange = Boolean(startDate && endDate);
  const incomeStatementAccounts = hasRange ? storedAccounts : [];
  const incomeStatementSummary = hasRange ? storedSummary : null;

  // --- Derived calculations ---
  const accounts = incomeStatementAccounts ?? [];

  // `account` is a nullable relation: a row whose account was deleted still
  // reports a total, and reading through it used to take the whole page down.
  const calculateTotal = (category) =>
    accounts
      .filter((item) => item.account?.category === category)
      .reduce((sum, item) => sum + item.total, 0);

  const incomeTotal = calculateTotal("INCOME");
  const costOfGoodsSoldTotal = calculateTotal("COST_OF_GOODS_SOLD");
  const grossProfit = incomeTotal - costOfGoodsSoldTotal;
  const expenseTotal = calculateTotal("EXPENSE");
  const operatingProfit = grossProfit - expenseTotal;
  const otherIncomeTotal = calculateTotal("OTHER_INCOME");

  const byCategory = (category) =>
    accounts
      .filter((item) => item.account?.category === category)
      .sort((a, b) =>
        (a.account?.code || "").localeCompare(b.account?.code || ""),
      );

  // Corporate income tax (990xxx) is booked as OTHER_EXPENSE but reported on
  // its own line below the profit before tax, as SAK EP requires.
  const isIncomeTax = (item) => (item.account?.code || "").startsWith("990");
  const sumTotal = (rows) => rows.reduce((sum, item) => sum + item.total, 0);

  const costOfGoodsSoldData = byCategory("COST_OF_GOODS_SOLD");
  const expenseData = byCategory("EXPENSE");
  const otherIncomeData = byCategory("OTHER_INCOME");
  const otherExpenseData = byCategory("OTHER_EXPENSE").filter(
    (item) => !isIncomeTax(item),
  );
  const incomeTaxData = byCategory("OTHER_EXPENSE").filter(isIncomeTax);

  // Prefer the API's totals (shared with mobile and the PDF export); the local
  // sums only cover a response that predates them.
  const summary = incomeStatementSummary ?? {};
  const otherExpenseTotal =
    summary.otherExpenseNonTax ?? sumTotal(otherExpenseData);
  const profitBeforeTax =
    summary.profitBeforeTax ??
    operatingProfit + otherIncomeTotal - otherExpenseTotal;
  const taxExpense = summary.taxExpense ?? sumTotal(incomeTaxData);
  const netProfit = summary.netIncome ?? profitBeforeTax - taxExpense;

  // --- Effects ---
  useEffect(() => {
    return () => {
      if (request) cancelAPIRequest(request);
      clearExportIncomeStatement(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user.mainBusiness?.id) return;
    if ((startDate && !endDate) || (!startDate && endDate)) return;
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.mainBusiness?.id, startDate, endDate]);

  useEffect(() => {
    if (incomeExportFile?.key) {
      downloadFile(incomeExportFile.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeExportFile]);

  useEffect(() => {
    if (incomeExportError) {
      setShowExportError(true);
    }
  }, [incomeExportError]);

  // --- Handlers ---
  const onLoad = () => {
    if (request) cancelAPIRequest(request);
    if (user.mainBusiness?.id && startDate && endDate) {
      requestListIncomeStatement(accountingDispatch, {
        businessID: user.mainBusiness.id,
        startDate: wibStartOfDay(startDate),
        endDate: wibEndOfDay(endDate),
      });
    }
  };

  const handleExport = useCallback(
    (exportType) => {
      if (!startDate || !endDate) {
        alert(t("accounting.transactions.filter.dateRange"));
        return;
      }
      if (user.mainBusiness?.id) {
        requestExportIncomeStatement(accountingDispatch, {
          businessID: user.mainBusiness.id,
          startDate: wibStartOfDay(startDate),
          endDate: wibEndOfDay(endDate),
          exportType,
        });
      }
    },
    [accountingDispatch, endDate, startDate, t, user.mainBusiness?.id],
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    setDownloadProgress(totalBytes ? transferredBytes / totalBytes : 0);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const req = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { onProgress: onDownloadProgress },
      });
      downloadRef.current = req;
      const result = await req.result;
      downloadBlob(result.body, getAccountingFileName(key));
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearExportIncomeStatement(accountingDispatch);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { useAccelerateEndpoint: true },
      });
      window.open(urlResult.url.href);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
      setShowDownloadErrorModal(true);
    }
  };

  const exportActionItems = EXPORTS.map((item) => ({
    name: t(item.label),
    onClick: () => handleExport(item.value),
  }));

  // --- Table helpers ---
  const dateParams =
    startDate && endDate
      ? `&startDate=${wibStartOfDay(startDate)}&endDate=${wibEndOfDay(endDate)}`
      : "";

  const TableSection = ({
    categoryName,
    label = t(accountCategoryLabel(categoryName)),
    rows = byCategory(categoryName),
    totalAmount,
  }) => {
    return (
      <>
        <tr className="bg-gray-50">
          <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left">
            {label}
          </th>
          <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
            {t("Amount")}
          </th>
        </tr>
        {rows.map((item, index) => {
          const { id, code, name } = item.account || {};
          return (
            <tr key={index}>
              <td className="pl-8 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                {code ? (
                  <a
                    href={`/v2/accounting/transactions?accountId=${id}${dateParams}`}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {`${code} - ${name}`}
                  </a>
                ) : (
                  <span>{name}</span>
                )}
              </td>
              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                <NumericFormat
                  displayType="text"
                  value={item.total}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  prefix="Rp"
                />
              </td>
            </tr>
          );
        })}
        <tr className="bg-gray-50">
          <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
            {`Total ${label}`}
          </th>
          <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
            <NumericFormat
              displayType="text"
              value={totalAmount}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              prefix="Rp"
            />
          </td>
        </tr>
      </>
    );
  };

  const EmptyTableSection = ({
    categoryName,
    label = t(accountCategoryLabel(categoryName)),
    totalAmount,
  }) => (
    <tr className="bg-gray-50">
      <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
        {`Total ${label}`}
      </th>
      <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
        <NumericFormat
          displayType="text"
          value={totalAmount}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="Rp"
        />
      </td>
    </tr>
  );

  const ProfitSection = ({
    categoryName,
    label = t(accountCategoryLabel(categoryName)),
    totalAmount,
    className,
  }) => (
    <tr className={className}>
      <th className="px-4 py-2 font-medium text-sm text-white whitespace-nowrap">
        {label}
      </th>
      <td className="px-4 py-2 font-medium text-sm text-white whitespace-nowrap text-right">
        <NumericFormat
          displayType="text"
          value={totalAmount}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="Rp"
        />
      </td>
    </tr>
  );

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="Income Statement"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => handleExport("pdf"),
            items: exportActionItems,
            disabled: incomeExportLoading,
          }}
        />
        <ReportRevisionNotice
          businessID={user.mainBusiness?.id}
          startDate={startDate}
          endDate={endDate}
          className="border-b border-gray-200"
        />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {/* Date filter */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDateRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              placeholderText={t("accounting.transactions.filter.dateRange")}
              onClear={() => setDateRange([null, null])}
              disabled={incomeStatementLoading}
            />
          </div>

          {/* Grouped table */}
          <div className="flex flex-col">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("Income")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                          {t("Amount")}
                        </th>
                      </tr>
                    </thead>
                    {incomeStatementLoading ? (
                      <TableSkeleton rows={6} columns={2} />
                    ) : (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {accounts.length > 0 ? (
                          <>
                            {/* Income accounts */}
                            {byCategory("INCOME").map((item, index) => {
                              const { id, code, name } = item.account || {};
                              return (
                                <tr key={index}>
                                  <td className="pl-8 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    {code ? (
                                      <a
                                        href={`/v2/accounting/transactions?accountId=${id}${dateParams}`}
                                        className="text-sm text-blue-600 hover:underline"
                                      >
                                        {`${code} - ${name}`}
                                      </a>
                                    ) : (
                                      <span>{name}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                    <NumericFormat
                                      displayType="text"
                                      value={item.total}
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      decimalScale={2}
                                      prefix="Rp"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Total Income */}
                            <tr className="bg-gray-50">
                              <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
                                {`Total ${t("Income")}`}
                              </th>
                              <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
                                <NumericFormat
                                  displayType="text"
                                  value={incomeTotal}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  decimalScale={2}
                                  prefix="Rp"
                                />
                              </td>
                            </tr>

                            {/* COGS */}
                            {costOfGoodsSoldData.length > 0 ? (
                              <TableSection
                                categoryName="COST_OF_GOODS_SOLD"
                                totalAmount={costOfGoodsSoldTotal}
                              />
                            ) : (
                              <EmptyTableSection
                                categoryName="COST_OF_GOODS_SOLD"
                                totalAmount={costOfGoodsSoldTotal}
                              />
                            )}

                            <ProfitSection
                              categoryName="GROSS_PROFIT"
                              totalAmount={grossProfit}
                              className="bg-blue-600"
                            />

                            {/* Expense */}
                            {expenseData.length > 0 ? (
                              <TableSection
                                categoryName="EXPENSE"
                                totalAmount={expenseTotal}
                              />
                            ) : (
                              <EmptyTableSection
                                categoryName="EXPENSE"
                                totalAmount={expenseTotal}
                              />
                            )}

                            <ProfitSection
                              categoryName="OPERATING_PROFIT"
                              totalAmount={operatingProfit}
                              className="bg-blue-600"
                            />

                            {/* Other Income */}
                            {otherIncomeData.length > 0 ? (
                              <TableSection
                                categoryName="OTHER_INCOME"
                                totalAmount={otherIncomeTotal}
                              />
                            ) : (
                              <EmptyTableSection
                                categoryName="OTHER_INCOME"
                                totalAmount={otherIncomeTotal}
                              />
                            )}

                            {/* Other Expense */}
                            {otherExpenseData.length > 0 ? (
                              <TableSection
                                categoryName="OTHER_EXPENSE"
                                rows={otherExpenseData}
                                totalAmount={otherExpenseTotal}
                              />
                            ) : (
                              <EmptyTableSection
                                categoryName="OTHER_EXPENSE"
                                totalAmount={otherExpenseTotal}
                              />
                            )}

                            <ProfitSection
                              label={t("Profit Before Tax")}
                              totalAmount={profitBeforeTax}
                              className="bg-blue-600"
                            />

                            {/* Income tax expense (990xxx) */}
                            {incomeTaxData.length > 0 ? (
                              <TableSection
                                label={t("Income Tax Expense")}
                                rows={incomeTaxData}
                                totalAmount={taxExpense}
                              />
                            ) : (
                              <EmptyTableSection
                                label={t("Income Tax Expense")}
                                totalAmount={taxExpense}
                              />
                            )}

                            <ProfitSection
                              categoryName="NET_PROFIT"
                              totalAmount={netProfit}
                              className="bg-blue-600"
                            />
                          </>
                        ) : (
                          <ErrorStateTable
                            otherError={incomeStatementError}
                            onRetry={onLoad}
                          />
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Download progress modal */}
        <Modal
          title="Download File"
          open={downloading}
          onClose={() => {
            if (downloadRef.current) downloadRef.current.cancel();
            setDownloading(false);
          }}
          footer={
            <div className="w-full">
              <p className="text-xs text-slate-500 mb-1">{t("Downloading")}</p>
              <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(downloadProgress * 100))}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          }
        >
          <div className="flex justify-center py-6">
            <DocumentIcon className="h-24 w-24 text-slate-400" />
          </div>
        </Modal>

        {/* Error modals */}
        {downloadDocError && (
          <ErrorDialog
            open={showDownloadErrorModal}
            error={downloadDocError}
            onClose={() => {
              setShowDownloadErrorModal(false);
              setDownloadDocError(null);
            }}
          />
        )}
        {incomeExportError && (
          <ErrorDialog
            open={showExportError}
            error={incomeExportError}
            onClose={() => {
              setShowExportError(false);
              clearExportIncomeStatement(accountingDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingIncomeStatement;
